import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  assembleJournalTextFor,
  canMarkJournalWebdoc,
  emptyJournalHeadingsFor,
  JOURNAL_MISSING_MARKER,
  JOURNAL_SECTION_MAX,
  JOURNAL_STODORD_MAX,
  JOURNAL_ACTION_PLAN_LEGACY_NOTICE,
  JOURNAL_ACTION_PLAN_LOCKED_HINT,
  legacyGeneratedSectionText,
  journalPrefillText,
  journalWebdocStatusLine,
  WEBDOC_JOURNAL_LABEL,
  type JournalAiMode,
  type JournalFinalized,
  type JournalSectionAiResult,
  type JournalLayout,
  type JournalNewSource,
  type JournalSection,
  type JournalSectionSource,
  type JournalStodord,
  type JournalWebdocStatus,
  type MeetingPrescriptionOrder,
  type UpdateJournalSections,
} from "@care-by-ella/contracts";
import { sourceDescriptions } from "@/lib/journal-source";
import { UnauthorizedError } from "@/lib/clinician-api";
import { isUntouchedPrefill } from "@/lib/journal-ai-gate";
import {
  autoSummaryProgressLabel,
  summariseUntouchedPrefill,
} from "@/lib/journal-auto-summary";
import { journalSaveState } from "@/lib/unsaved-journal";
import { useUnsavedJournal } from "./UnsavedJournalGuard";
import { useReadOnly } from "./SupportView";
import { JournalNewSourceReview } from "./JournalNewSourceReview";

// TES-904 — the sectioned journal note, shared by the care meeting
// (MeetingJournalCard, steps 1–3) and the monthly review
// (RenewalReviewPanel, step 4). The card owns the boxes, saving
// and "Sammanställ journal"; the caller supplies where the note is read from
// and saved to, and anything to offer above the boxes (the meeting's AI
// drafts).
//
// Shaped by TES-941 §JOURNALANTECKNINGEN:
//   "Alla journalrubriker ska vara synliga samtidigt"
//   "Under varje rubrik ska det endast finnas en ren, redigerbar ruta"
//   "Undvik återkommande instruktionstext om AI, stödord och Webdoc vid varje fält."
//   "Det ska finnas en enda knapp längst ner: ”Sammanställ journal”"
// So: every heading always open, one box each, no help text under them.
// TES-978 — the two notes have different headings (the meeting's seven, the
// monthly review's ten); the caller's `layout` says which.
// The server prefills the empty boxes (from her forms for a meeting, from her
// monthly check-in for a monthly review); what the doctor writes is saved
// when she leaves a box, and before the note is assembled or the decision
// taken.
//
// TES-978 "JOURNALENS KÄLLOR OCH VERSIONER" — every box shows where its
// prefill came from ("Källa"), and a newer prefill is offered beside the
// doctor's text, never written over it (since TES-1244 as "Granska
// kompletteringen" / "Granska informationen", JournalNewSourceReview). The shorthand
// ("Stödord") is part of the note and autosaves. "Sammanställ journal" stores
// the copied text as the note's final version, which the doctor can then
// mark "Införd i Webdoc" — his receipt for that version, not a check of
// Webdoc. A later change to the note says the latest text is not marked.
//
// TES-1019 (Emelie, points 3–6) — the care meeting's note drops the shorthand
// and the AI "Skriv ut" that routed it, and is assembled, copied and
// confirmed in the Webdoc step instead of by "Sammanställ journal" here. So
// the shorthand box shows only for a caller that saves it (`saveStodord`),
// and the button and the receipt only for a caller that stores versions
// (`finalize` + `markWebdoc`). Since TES-1034 the monthly review passes
// none of the three either (its Webdoc step compiles and confirms the note),
// so no caller shows them today; they stay for a note that asks.
//
// TES-1328 (A-02 2026-10-13) — a layout's `generatedSection` ("Åtgärd och
// behandlingsplan") is locked: no box, only the fixed hint, since its text is
// generated when the note is compiled. The "Ordination" section under the
// boxes is gone, with its internal texts.

export type JournalValues = Record<string, string>;

export interface JournalSectionsHandle {
  /** Saves every unsaved box; resolves to the note as it now stands. */
  flush(): Promise<JournalValues>;
  /**
   * TES-1312 — before the note is composed: summarises every heading still
   * holding its untouched prefill through the per-heading AI, then saves.
   * Resolves to the headings (keys) still raw; when there are any, the card
   * shows its notice and the caller must not compose (unless the doctor
   * presses "Sammanställ ändå", which calls `onCompileAnyway`).
   */
  summariseRaw(
    onProgress?: (current: number, total: number) => void,
  ): Promise<{ raw: string[] }>;
  /** TES-1312 — the headings whose text is still the AI's automatic summary. */
  aiSummarisedHeadings(): string[];
}

// TES-1312 — the tag beside a heading the AI summarised on compile, in the
// admin only: it is never part of the text copied to Webdoc.
export const AI_SUMMARISED_TAG = "AI-sammanfattad – granska";

export function AiSummarisedHeadings({ headings }: { headings: string[] }) {
  if (headings.length === 0) return null;
  return (
    <ul
      className="flex flex-wrap gap-xs text-xs"
      aria-label="AI-sammanfattade rubriker"
    >
      {headings.map((h) => (
        <li
          key={h}
          className="rounded-pill border border-warning/40 bg-warning/10 px-sm py-[2px] text-textPrimary"
        >
          <span className="font-semibold">{h}</span> · {AI_SUMMARISED_TAG}
        </li>
      ))}
    </ul>
  );
}

// The boxes the doctor writes in: every heading but a generated one.
function writableKeys(layout: JournalLayout): readonly string[] {
  return layout.keys.filter((k) => k !== layout.generatedSection);
}

function emptyValues(layout: JournalLayout): JournalValues {
  return Object.fromEntries(layout.keys.map((k) => [k, ""]));
}

const TEXTAREA =
  "w-full rounded-md border border-surfaceMuted bg-surface p-sm text-sm leading-relaxed text-textPrimary placeholder:text-textDisabled focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20";

// Grows with the text so the whole note can be scanned without scrolling
// inside a box; long boxes stop growing and scroll.
function rowsFor(value: string): number {
  return Math.min(14, Math.max(2, value.split("\n").length + 1));
}

// The prefill a box was built from, as text, or null when it had none.
function prefillOf(source: JournalSectionSource | null | undefined) {
  return source ? journalPrefillText(source.lines) : null;
}

// TES-1312 — whether a box still holds the AI summary written into it on
// compile.
function isAutoApplied(current: string | undefined, applied?: string) {
  return applied !== undefined && (current ?? "").trim() === applied.trim();
}

function time(d: Date): string {
  return d.toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" });
}

export interface JournalSectionsCardProps {
  /** Unique per note: prefixes the boxes' element ids. */
  idPrefix: string;
  /** The note's headings in order, and its plan box (TES-978). Stable. */
  layout: JournalLayout;
  /** Reads the note; the server creates and prefills it on first read. Stable (useCallback). */
  load: () => Promise<{
    sections: JournalSection[];
    stodord?: JournalStodord | null;
    webdoc?: JournalWebdocStatus;
  }>;
  /** Saves the boxes that changed. Stable (useCallback). */
  save: (input: UpdateJournalSections) => Promise<{
    sections: JournalSection[];
    webdoc?: JournalWebdocStatus;
  }>;
  /**
   * TES-978 — autosaves the shorthand. Stable (useCallback). Without it the
   * card has no "Stödord" box (the care meeting since TES-1019).
   */
  saveStodord?: (
    content: string,
  ) => Promise<{ stodord: JournalStodord | null }>;
  /**
   * TES-978 — stores the assembled text as the final version. Stable. With
   * `markWebdoc`, it gives the card its "Sammanställ journal" and receipt;
   * without them (the care meeting since TES-1019) the card has neither.
   */
  finalize?: (text: string) => Promise<JournalFinalized>;
  /** TES-978 — marks a final version "Införd i Webdoc". Stable. */
  markWebdoc?: (revisionId: string) => Promise<JournalWebdocStatus>;
  onUnauthorized: () => void;
  // TES-956 — the order on screen. Assembling the note writes it as the first
  // line of the layout's plan box ("Åtgärd och beslut"), or, with a
  // `generatedSection` (TES-1328), as planned medication in it; the boxes
  // themselves are never written to.
  order?: MeetingPrescriptionOrder | null;
  // TES-978 — the follow-up decided on screen, as one line. Assembling the
  // note writes it as the first line of the layout's follow-up box
  // ("Nästa uppföljning"); the box itself is never written to.
  followUpLine?: string | null;
  /**
   * TES-1019 — the AI for one heading at a time. Absent when the AI switch is
   * off: the note is then exactly the deterministic prefill. Stable
   * (useCallback).
   * TES-1294 — no longer a button under each box (A-02: "Ta bort Sammanfatta
   * och Sammanställ efter varje enskilt journalfält"); it is only called by
   * `summariseRaw` from the single "Sammanställ journalanteckning".
   */
  sectionAi?: (
    key: string,
    mode: JournalAiMode,
    text: string,
  ) => Promise<JournalSectionAiResult>;
  /**
   * TES-1312 — "Sammanställ ändå" on the notice that headings are still raw:
   * the caller composes the note without summarising again.
   */
  onCompileAnyway?: () => void;
}

export const JournalSectionsCard = forwardRef<
  JournalSectionsHandle,
  JournalSectionsCardProps
>(function JournalSectionsCard(
  {
    idPrefix,
    layout,
    load: loadNote,
    save: saveNote,
    saveStodord: saveStodordNote,
    finalize,
    markWebdoc,
    onUnauthorized,
    order,
    followUpLine,
    sectionAi,
    onCompileAnyway,
  },
  ref,
) {
  // TES-1040 — the admin's read-only view shows the note as it stands:
  // nothing editable, nothing saved, no AI, no compile, no Webdoc tick.
  const readOnly = useReadOnly();
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [values, setValues] = useState<JournalValues>(() =>
    emptyValues(layout),
  );
  // Refs mirror the state so that flush() — called from the parent's submit —
  // sees what is on screen now, not what was there when it was created.
  const valuesRef = useRef<JournalValues>(values);
  const dirtyRef = useRef<Set<string>>(new Set());
  const [unsaved, setUnsaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [assembled, setAssembled] = useState<{
    text: string;
    skipped: string[];
    copied: boolean;
    // The stored final version, or false when storing it failed.
    version: JournalFinalized | false;
  } | null>(null);
  // TES-978 — which final version is marked "Införd i Webdoc".
  const [webdoc, setWebdoc] = useState<JournalWebdocStatus | null>(null);
  const [marking, setMarking] = useState(false);
  const [markError, setMarkError] = useState<string | null>(null);
  // TES-978 — per box: the prefill it came from, and a newer one if any.
  const [meta, setMeta] = useState<
    Record<
      string,
      {
        source: JournalSectionSource | null;
        newSource: JournalNewSource | null;
        // TES-1040 — computed for an admin's support read, not stored.
        preview: boolean;
        // TES-1244 — a clinician has saved this box.
        edited: boolean;
      }
    >
  >({});
  const metaRef = useRef<typeof meta>({});
  // TES-1312 — per box, the AI summary written into it on compile; the tag
  // shows while the box still holds exactly that text.
  const autoAppliedRef = useRef<Record<string, string>>({});
  const [autoApplied, setAutoApplied] = useState<Record<string, string>>({});
  // TES-1312 — the headings left raw by the last compile, and the progress
  // of a run under way (for the card's own button).
  const [rawNotice, setRawNotice] = useState<string[] | null>(null);
  const [summaryProgress, setSummaryProgress] = useState<string | null>(null);
  const [summarising, setSummarising] = useState(false);
  const noticeRef = useRef<HTMLDivElement>(null);
  // TES-978 — the shorthand, autosaved a moment after typing stops.
  const [stodord, setStodord] = useState("");
  const stodordRef = useRef("");
  const stodordDirtyRef = useRef(false);
  const stodordTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Mirrors stodordDirtyRef for the navigation guard.
  const [stodordPending, setStodordPending] = useState(false);
  const [stodordStatus, setStodordStatus] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");

  // Read through a ref so a caller passing an inline sign-out callback does
  // not make every render refetch the note.
  const onUnauthorizedRef = useRef(onUnauthorized);
  useEffect(() => {
    onUnauthorizedRef.current = onUnauthorized;
  }, [onUnauthorized]);

  const apply = useCallback(
    (journal: { sections: JournalSection[]; webdoc?: JournalWebdocStatus }) => {
      if (journal.webdoc) setWebdoc(journal.webdoc);
      // A box she is typing in keeps her text; the server's copy only fills
      // the others.
      const next = { ...valuesRef.current };
      const nextMeta: Record<
        string,
        {
          source: JournalSectionSource | null;
          newSource: JournalNewSource | null;
          preview: boolean;
          edited: boolean;
        }
      > = {};
      for (const s of journal.sections) {
        if (!dirtyRef.current.has(s.key)) next[s.key] = s.content;
        nextMeta[s.key] = {
          source: s.source ?? null,
          newSource: s.newSource ?? null,
          preview: s.preview === true,
          edited: s.editedAt != null,
        };
      }
      valuesRef.current = next;
      setValues(next);
      metaRef.current = nextMeta;
      setMeta(nextMeta);
    },
    [],
  );

  const load = useCallback(() => {
    setLoadError(null);
    loadNote()
      .then((j) => {
        apply(j);
        if (!stodordDirtyRef.current) {
          stodordRef.current = j.stodord?.content ?? "";
          setStodord(stodordRef.current);
        }
        setLoaded(true);
      })
      .catch((e) => {
        if (e instanceof UnauthorizedError) onUnauthorizedRef.current();
        else setLoadError("Kunde inte hämta journalen.");
      });
  }, [loadNote, apply]);

  useEffect(load, [load]);

  const save = useCallback(async (): Promise<void> => {
    const keys = [...dirtyRef.current];
    if (keys.length === 0) return;
    const sent = keys.map((key) => ({
      key,
      content: valuesRef.current[key] ?? "",
    }));
    setSaving(true);
    setSaveError(null);
    try {
      const journal = await saveNote({ sections: sent });
      // Only what was sent AND is still what is on screen counts as saved; a
      // keystroke during the request stays dirty for the next save.
      for (const s of sent) {
        if (valuesRef.current[s.key] === s.content) {
          dirtyRef.current.delete(s.key);
        }
      }
      apply(journal);
      setUnsaved(dirtyRef.current.size > 0);
      setSavedAt(new Date());
    } catch (e) {
      if (e instanceof UnauthorizedError) onUnauthorizedRef.current();
      setSaveError("Journalen kunde inte sparas. Försök igen.");
      throw e;
    } finally {
      setSaving(false);
    }
  }, [saveNote, apply]);

  // Saves the shorthand now if it changed since the last save.
  const flushStodord = useCallback(async (): Promise<void> => {
    if (stodordTimerRef.current) {
      clearTimeout(stodordTimerRef.current);
      stodordTimerRef.current = null;
    }
    if (!stodordDirtyRef.current || !saveStodordNote) return;
    const sent = stodordRef.current;
    setStodordStatus("saving");
    try {
      await saveStodordNote(sent);
      if (stodordRef.current === sent) {
        stodordDirtyRef.current = false;
        setStodordPending(false);
      }
      setStodordStatus("saved");
    } catch (e) {
      if (e instanceof UnauthorizedError) onUnauthorizedRef.current();
      setStodordStatus("error");
      throw e;
    }
  }, [saveStodordNote]);

  // Leaving the screen mid-sentence still saves what was typed.
  useEffect(
    () => () => {
      if (stodordDirtyRef.current) void flushStodord().catch(() => undefined);
    },
    [flushStodord],
  );

  const changeStodord = (value: string) => {
    if (readOnly) return;
    stodordRef.current = value;
    stodordDirtyRef.current = true;
    setStodordPending(true);
    setStodord(value);
    setStodordStatus("idle");
    if (stodordTimerRef.current) clearTimeout(stodordTimerRef.current);
    stodordTimerRef.current = setTimeout(
      () => void flushStodord().catch(() => undefined),
      1000,
    );
  };

  const summariseRaw = useCallback(
    async (
      onProgress?: (current: number, total: number) => void,
    ): Promise<{ raw: string[] }> => {
      if (readOnly) return { raw: [] };
      setRawNotice(null);
      setSummarising(true);
      try {
        const outcome = await summariseUntouchedPrefill({
          // TES-1328 — the locked, generated box is never summarised.
          boxes: writableKeys(layout).map((key) => ({
            key,
            current: valuesRef.current[key] ?? "",
            prefill: prefillOf(metaRef.current[key]?.source),
          })),
          readOnly,
          // The same call as "Sammanfatta" under a box.
          generate: sectionAi
            ? (key) => sectionAi(key, "summary", valuesRef.current[key] ?? "")
            : undefined,
          currentOf: (key) => valuesRef.current[key] ?? "",
          // The same path as "Använd" under a box: the box changes, and the
          // save below stores it.
          apply: (key, text) => {
            change(key, text);
            autoAppliedRef.current = {
              ...autoAppliedRef.current,
              [key]: text,
            };
            setAutoApplied(autoAppliedRef.current);
          },
          onProgress,
          isFatal: (e) => e instanceof UnauthorizedError,
        });
        await save();
        if (outcome.raw.length > 0) {
          setRawNotice(outcome.raw);
          // The notice may sit far from the button that was pressed.
          requestAnimationFrame(() =>
            noticeRef.current?.scrollIntoView({
              block: "center",
              behavior: "smooth",
            }),
          );
        }
        return { raw: outcome.raw };
      } catch (e) {
        if (e instanceof UnauthorizedError) onUnauthorizedRef.current();
        throw e;
      } finally {
        setSummarising(false);
      }
    },
    // `change` only touches refs, `readOnly` and stable setters.
    [readOnly, layout, sectionAi, save],
  );

  const aiSummarisedHeadings = useCallback(
    () =>
      layout.keys
        .filter((key) =>
          isAutoApplied(valuesRef.current[key], autoAppliedRef.current[key]),
        )
        .map((key) => layout.headings[key]),
    [layout],
  );

  useImperativeHandle(
    ref,
    () => ({
      async flush() {
        await save();
        await flushStodord();
        return valuesRef.current;
      },
      summariseRaw,
      aiSummarisedHeadings,
    }),
    [save, flushStodord, summariseRaw, aiSummarisedHeadings],
  );

  // TES-1312 — the headings of the notice that still hold raw answers now:
  // one he rewrites leaves the list as he does it.
  const rawNow = (rawNotice ?? []).filter((key) =>
    isUntouchedPrefill({
      current: values[key] ?? "",
      prefill: prefillOf(meta[key]?.source),
    }),
  );
  const focusHeading = (key: string) =>
    document.getElementById(`journal-${idPrefix}-${key}`)?.focus();

  const change = (key: string, value: string) => {
    if (readOnly) return;
    valuesRef.current = { ...valuesRef.current, [key]: value };
    dirtyRef.current.add(key);
    setValues(valuesRef.current);
    setUnsaved(true);
    setAssembled(null);
  };

  // TES-1244 — new material reviewed: the approved proposal replaces the box,
  // or he keeps his text as it is. Either is a save, which is what settles
  // the flag.
  const approveNewSource = (key: string, text: string) => {
    change(key, text);
    void save().catch(() => undefined);
  };
  const keepOwnText = (key: string) => {
    dirtyRef.current.add(key);
    void save().catch(() => undefined);
  };

  async function compile(force = false) {
    try {
      if (!force) {
        const { raw } = await summariseRaw((current, total) =>
          setSummaryProgress(autoSummaryProgressLabel(current, total)),
        );
        if (raw.length > 0) return;
      }
      await save();
    } catch {
      return;
    } finally {
      setSummaryProgress(null);
    }
    const sections = layout.keys.map((key) => ({
      key,
      content: valuesRef.current[key] ?? "",
    }));
    const text = assembleJournalTextFor(layout, sections, {
      order,
      followUp: followUpLine,
    });
    let copied = false;
    if (text) {
      try {
        await navigator.clipboard.writeText(text);
        copied = true;
      } catch {
        // No clipboard permission: the text is shown below to copy by hand.
      }
    }
    // TES-978 — the copied text is stored as the note's final version.
    let version: JournalFinalized | false = false;
    if (text && finalize) {
      try {
        version = await finalize(text);
        setWebdoc(version.webdoc);
        setMarkError(null);
      } catch (e) {
        if (e instanceof UnauthorizedError) onUnauthorizedRef.current();
      }
    }
    setAssembled({
      text,
      skipped: emptyJournalHeadingsFor(layout, sections, {
        order,
        followUp: followUpLine,
      }),
      copied,
      version,
    });
  }

  async function mark(revisionId: string) {
    if (!markWebdoc) return;
    setMarking(true);
    setMarkError(null);
    try {
      setWebdoc(await markWebdoc(revisionId));
    } catch (e) {
      if (e instanceof UnauthorizedError) onUnauthorizedRef.current();
      else
        setMarkError(
          "Markeringen kunde inte sparas. Sammanställ journalen igen och försök på nytt.",
        );
    } finally {
      setMarking(false);
    }
  }

  const stodordText =
    stodordStatus === "saving"
      ? "Sparar…"
      : stodordStatus === "error"
        ? "Stödorden kunde inte sparas. Försök igen."
        : stodordStatus === "saved"
          ? "Sparade"
          : null;

  // TES-978 — the navigation guard warns while this text is not saved: the
  // boxes and the shorthand alike.
  useUnsavedJournal(
    journalSaveState({
      unsaved: unsaved || stodordPending,
      saving: saving || stodordStatus === "saving",
      failed: saveError !== null || stodordStatus === "error",
    }),
  );

  const status = saving
    ? "Sparar…"
    : saveError
      ? saveError
      : unsaved
        ? "Ändringar sparas när du lämnar rutan"
        : savedAt
          ? `Sparad ${time(savedAt)}`
          : null;

  return (
    <section className="rounded-lg bg-surface p-md shadow-[0_1px_2px_rgba(42,26,40,0.06),0_8px_24px_-16px_rgba(97,32,95,0.25)]">
      <div className="flex items-baseline justify-between gap-md">
        <h2 className="font-display text-[20px] leading-7 text-textPrimary">
          Journalanteckning
        </h2>
        {status ? (
          <span
            className={`text-xs ${saveError ? "text-danger" : "text-textSecondary"}`}
            aria-live="polite"
          >
            {status}
          </span>
        ) : null}
      </div>

      {saveStodordNote ? (
        <div className="mt-sm">
          <div className="flex items-baseline justify-between gap-sm">
            <label
              htmlFor={`journal-${idPrefix}-stodord`}
              className="text-sm font-semibold text-textPrimary"
            >
              Stödord
            </label>
            {stodordText ? (
              <span
                className={`text-xs ${stodordStatus === "error" ? "text-danger" : "text-textSecondary"}`}
                aria-live="polite"
              >
                {stodordText}
              </span>
            ) : null}
          </div>
          <textarea
            id={`journal-${idPrefix}-stodord`}
            value={stodord}
            onChange={(e) => changeStodord(e.target.value)}
            onBlur={() => void flushStodord().catch(() => undefined)}
            rows={3}
            maxLength={JOURNAL_STODORD_MAX}
            disabled={!loaded}
            readOnly={readOnly}
            className={`mt-xs ${TEXTAREA}`}
          />
        </div>
      ) : null}

      {loadError ? (
        <div className="mt-sm rounded-md border border-danger/30 bg-danger/10 p-sm text-sm text-danger">
          {loadError}{" "}
          <button type="button" onClick={load} className="underline">
            Försök igen
          </button>
        </div>
      ) : !loaded ? (
        <p className="mt-sm text-sm text-textSecondary">Hämtar journal…</p>
      ) : (
        <div className="mt-sm flex flex-col gap-md">
          {layout.keys.map((key) => {
            const id = `journal-${idPrefix}-${key}`;
            if (key === layout.generatedSection) {
              return (
                <LockedSection
                  key={key}
                  id={id}
                  heading={layout.headings[key]}
                  legacyText={legacyGeneratedSectionText(
                    values[key],
                    prefillOf(meta[key]?.source),
                  )}
                />
              );
            }
            return (
              <div key={key}>
                <div className="mb-xs flex flex-wrap items-baseline gap-sm">
                  <label
                    htmlFor={id}
                    className="block text-sm font-semibold text-textPrimary"
                  >
                    {layout.headings[key]}
                  </label>
                  {isAutoApplied(values[key], autoApplied[key]) ? (
                    <span className="rounded-pill border border-warning/40 bg-warning/10 px-sm py-[1px] text-[11px] text-textPrimary">
                      {AI_SUMMARISED_TAG}
                    </span>
                  ) : null}
                </div>
                <textarea
                  id={id}
                  value={values[key] ?? ""}
                  onChange={(e) => change(key, e.target.value)}
                  onBlur={() => {
                    if (dirtyRef.current.has(key))
                      void save().catch(() => undefined);
                  }}
                  rows={rowsFor(values[key] ?? "")}
                  maxLength={JOURNAL_SECTION_MAX}
                  readOnly={readOnly}
                  // TES-984 — Livsstil and Psykisk status with no basis:
                  // "Sammanställ journal" writes the marker under them.
                  placeholder={
                    layout.missingMarkerSections?.includes(key)
                      ? JOURNAL_MISSING_MARKER
                      : undefined
                  }
                  className={TEXTAREA}
                />
                {meta[key]?.preview ? (
                  <p className="mt-xs text-xs text-textSecondary">
                    Förhandsvisning — inte sparad ännu
                  </p>
                ) : null}
                {meta[key]?.newSource && !readOnly ? (
                  <JournalNewSourceReview
                    idPrefix={idPrefix}
                    sectionKey={key}
                    heading={layout.headings[key]}
                    newSource={meta[key]!.newSource!}
                    current={values[key] ?? ""}
                    edited={meta[key]!.edited}
                    sectionAi={sectionAi}
                    onApprove={(text) => approveNewSource(key, text)}
                    onKeep={() => keepOwnText(key)}
                    onUnauthorized={() => onUnauthorizedRef.current()}
                    disabled={saving}
                  />
                ) : null}
                {meta[key]?.source ? (
                  <SourceList source={meta[key]!.source!} />
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      {/* TES-1312 — the AI could not summarise these headings, so the note
          is not composed with her raw answers in it until he chooses. */}
      {rawNow.length > 0 && !readOnly ? (
        <div
          ref={noticeRef}
          role="alert"
          className="mt-md rounded-md border border-warning/40 bg-warning/10 p-sm text-sm"
        >
          <p className="text-textPrimary">
            {`Rubrikerna innehåller fortfarande patientens svar ordagrant och kunde inte sammanfattas: ${rawNow
              .map((key) => layout.headings[key])
              .join(", ")}.`}
          </p>
          <div className="mt-xs flex flex-wrap gap-xs">
            <button
              type="button"
              onClick={() => focusHeading(rawNow[0]!)}
              className="rounded-md border border-brand px-sm py-[3px] text-xs font-semibold text-brand"
            >
              Skriv om rubrikerna själv
            </button>
            <button
              type="button"
              onClick={() => {
                setRawNotice(null);
                if (finalize && markWebdoc) void compile(true);
                else onCompileAnyway?.();
              }}
              className="rounded-md border border-surfaceMuted px-sm py-[3px] text-xs text-textSecondary hover:text-textPrimary"
            >
              Sammanställ ändå
            </button>
          </div>
        </div>
      ) : null}

      {finalize && markWebdoc ? (
        <div className="mt-md border-t border-surfaceMuted pt-md">
          {readOnly ? null : (
            <button
              type="button"
              disabled={!loaded || saving || summarising}
              onClick={() => void compile()}
              className="w-full rounded-pill border border-brand px-md py-[10px] text-sm font-semibold text-brand hover:bg-brand/5 disabled:opacity-50"
            >
              {summaryProgress ?? "Sammanställ journalanteckning"}
            </button>
          )}
          {assembled ? (
            <div className="mt-sm" aria-live="polite">
              {assembled.text ? (
                <>
                  <p className="text-xs text-textSecondary">
                    {assembled.copied
                      ? "Kopierad — klistra in i Webdoc."
                      : "Kunde inte kopiera automatiskt. Markera texten nedan och kopiera den."}
                    {assembled.skipped.length > 0
                      ? ` Tomma rubriker togs inte med: ${assembled.skipped.join(", ")}.`
                      : ""}
                  </p>
                  <p
                    className={`mt-xs text-xs ${assembled.version ? "text-textSecondary" : "text-danger"}`}
                  >
                    {assembled.version
                      ? `Sparad som journalversion ${assembled.version.version} ${time(new Date(assembled.version.createdAt))}.`
                      : "Journalversionen kunde inte sparas. Sammanställ igen."}
                  </p>
                  <div className="mt-xs">
                    <AiSummarisedHeadings headings={aiSummarisedHeadings()} />
                  </div>
                  <textarea
                    readOnly
                    value={assembled.text}
                    rows={Math.min(16, assembled.text.split("\n").length + 1)}
                    onFocus={(e) => e.currentTarget.select()}
                    aria-label="Sammanställd journal"
                    className={`mt-xs font-mono text-xs ${TEXTAREA}`}
                  />
                </>
              ) : (
                <p className="text-xs text-textSecondary">
                  Inget att sammanställa ännu — alla rubriker är tomma.
                </p>
              )}
            </div>
          ) : null}
          {webdoc?.latestFinal ? (
            <WebdocReceipt
              status={webdoc}
              busy={marking}
              error={markError}
              onMark={() => void mark(webdoc.latestFinal!.revisionId)}
            />
          ) : null}
        </div>
      ) : null}
    </section>
  );
});

// TES-1328 (A-02 2026-10-13) — "Åtgärd och behandlingsplan" before compile:
// "Fältet ligger i den tidiga journalarbetsytan, innan recept, kompletteringar
// och uppföljning har kunnat sammanställas, och är därför låst. I detta steg
// visas endast hjälptexten [...] Inga exempelvärden eller någon preliminär
// journaltext ska visas." So: the heading and the hint, nothing to type in.
// Transition: text a doctor wrote in the box before the lock is shown
// read-only under the hint, since the compiled note keeps it.
export function LockedSection({
  id,
  heading,
  legacyText,
}: {
  id: string;
  heading: string;
  legacyText?: string;
}) {
  return (
    <section aria-labelledby={id}>
      <h3 id={id} className="mb-xs text-sm font-semibold text-textPrimary">
        {heading}
      </h3>
      <p className="rounded-md border border-surfaceMuted bg-surfaceMuted/40 p-sm text-sm text-textSecondary">
        {JOURNAL_ACTION_PLAN_LOCKED_HINT}
      </p>
      {legacyText ? (
        <div className="mt-xs">
          <p className="whitespace-pre-wrap rounded-md border border-surfaceMuted p-sm text-sm leading-relaxed text-textPrimary">
            {legacyText}
          </p>
          <p className="mt-xs text-xs text-textSecondary">
            {JOURNAL_ACTION_PLAN_LEGACY_NOTICE}
          </p>
        </div>
      ) : null}
    </section>
  );
}

// TES-978 — "Införd i Webdoc", on the version. Emelie: a manual receipt with
// doctor, version and time,
// "inte ett påstående om att Ella tekniskt har verifierat innehållet i Webdoc".
// So the line reports his marking, and when the note has changed since, that
// the latest text is not marked.
function WebdocReceipt({
  status,
  busy,
  error,
  onMark,
}: {
  status: JournalWebdocStatus;
  busy: boolean;
  error: string | null;
  onMark: () => void;
}) {
  const line = journalWebdocStatusLine(status);
  const readOnly = useReadOnly();
  const canMark = !readOnly && canMarkJournalWebdoc(status);
  return (
    <div className="mt-sm flex flex-col gap-xs text-xs" aria-live="polite">
      {line ? (
        <p
          className={
            status.changedSinceReceipt ? "text-warning" : "text-textSecondary"
          }
        >
          {line}
        </p>
      ) : null}
      {canMark ? (
        <button
          type="button"
          onClick={onMark}
          disabled={busy}
          className="self-start rounded-md border border-surfaceMuted px-sm py-xs font-semibold text-textPrimary hover:border-brand disabled:opacity-50"
        >
          {`${WEBDOC_JOURNAL_LABEL} (version ${status.latestFinal!.version})`}
        </button>
      ) : null}
      {error ? <p className="text-danger">{error}</p> : null}
    </div>
  );
}

// "Källa" under a box: each prefilled line's form, date, question and her
// original answer. Collapsed, so the headings stay scannable.
function SourceList({ source }: { source: JournalSectionSource }) {
  const lines = source.lines.flatMap(sourceDescriptions);
  if (lines.length === 0) return null;
  return (
    <details className="mt-xs text-xs text-textSecondary">
      <summary className="cursor-pointer">Källa</summary>
      <ul className="mt-xs flex flex-col gap-[2px]">
        {lines.map((l, i) => (
          <li key={i}>{l}</li>
        ))}
      </ul>
    </details>
  );
}
